import { useEffect, useState } from 'react';
import { useAusschreibungBoard } from '../../api/stellenausschreibungen';
import {
  AUSSCHREIBUNG_SPALTEN_ORDER,
  AusschreibungScope,
  AusschreibungSpalte,
} from '../../types/stellenausschreibung';
import { AusschreibungColumn } from './AusschreibungColumn';

const STORAGE_KEY = 'ausschreibungen-collapsed';

/** Das Archiv ist beim ersten Öffnen zugeklappt — es ist Nachschlagewerk, keine Arbeit. */
const DEFAULT_COLLAPSED: AusschreibungSpalte[] = [AusschreibungSpalte.ERLEDIGT];

interface Props {
  scope: AusschreibungScope;
}

export function AusschreibungBoard({ scope }: Props) {
  const { data, isLoading, error } = useAusschreibungBoard(scope);
  const [collapsed, setCollapsed] = useState<AusschreibungSpalte[]>(DEFAULT_COLLAPSED);

  useEffect(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) setCollapsed(JSON.parse(stored));
    } catch {
      // Kaputter oder gesperrter Storage darf das Board nicht kosten.
    }
  }, []);

  const toggle = (spalte: AusschreibungSpalte) => {
    setCollapsed((prev) => {
      const next = prev.includes(spalte)
        ? prev.filter((s) => s !== spalte)
        : [...prev, spalte];
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
      } catch {
        // s.o.
      }
      return next;
    });
  };

  if (isLoading) {
    return (
      <div className="py-16 text-center">
        <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" />
      </div>
    );
  }

  if (error) {
    return (
      <p className="py-16 text-center text-sm text-red-600">
        Stellenausschreibungen konnten nicht geladen werden
      </p>
    );
  }

  const cards = data?.data ?? [];
  // Ohne hinterlegte Zugangsdaten bleibt es beim Abhaken von Hand.
  const websitePublishVerfuegbar = !!data?.meta?.websitePublishVerfuegbar;

  return (
    <div className="flex gap-3 overflow-x-auto pb-2 xl:h-full">
      {AUSSCHREIBUNG_SPALTEN_ORDER.map((spalte) => (
        <AusschreibungColumn
          key={spalte}
          spalte={spalte}
          cards={cards.filter((c) => c.spalte === spalte)}
          isCollapsed={collapsed.includes(spalte)}
          onToggleCollapse={() => toggle(spalte)}
          websitePublishVerfuegbar={websitePublishVerfuegbar}
        />
      ))}
    </div>
  );
}
